import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Col, Form, Layout, Row, Typography } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { SiteBranding } from 'parts';
import { Button } from 'components';
import { ReactComponent as IconBack, ReactComponent as IconArrowLeft } from 'static/icons/arrow_left.svg';
import { ReactComponent as IconNext } from 'static/icons/arrow_right.svg';
import { postCheckEmail, postPlayerRegistration } from 'fetcher/player-registration';
import dayjs from 'dayjs';
import { PAGES } from 'router/constants';
import { extractFileObject } from 'components/Upload/utils';
import { ReactComponent as IconSuccess } from 'static/inons_large/success.svg';
import { INITIAL_VALUES, STEP_FIELDS, TRANSLATIONS } from './constants';
import { Step0 } from './Step0';
import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { Step3 } from './Step3';
import { Step4 } from './Step4';
import { Step5 } from './Step5';
import { Step6 } from './Step6';
import { Step7 } from './Step7';
import { Step8 } from './Step8';
import { Step9 } from './Step9';
import { Step10 } from './Step10';
import { loadModel, validateFace } from './utils';
import './style.scss';

type Language = 'en' | 'es';

const LanguageContext = createContext({
  lang: 'en' as Language,
  t: (key: string) => key,
});

export const useLang = () => useContext(LanguageContext);

export const PlayerRegistration: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState({
    loadingEmailCheck: false,
    loadingSave: false,
    loadingFaceValidation: false,
  });
  const [loadingModel, setLoadingModel] = useState(false);
  const [lang, setLang] = useState<Language>('en');

  const t = useCallback((key: string) => TRANSLATIONS[lang][key] || key, [lang]);

  const loading = useMemo(() => Object.values(requestsLoading).includes(true), [requestsLoading]);

  const prev = () => {
    setStep((s) => s - 1);
  };

  const next = async () => {
    try {
      const data = { ...form.getFieldsValue(true) };

      if (step === STEP_FIELDS.length - 1) {
        await form.validateFields(STEP_FIELDS[step]);
        setRequestsLoading((l) => ({ ...l, loadingSave: true }));

        data.phone = `+1${data.phone.replace(/\D/g, '')}`;
        if (data.guardian_phone) data.guardian_phone = `+1${data.guardian_phone.replace(/\D/g, '')}`;
        if (data.emergency_contact_phone)
          data.emergency_contact_phone = `+1${data.emergency_contact_phone.replace(/\D/g, '')}`;
        if (data.emergency_contact_first_name || data.emergency_contact_last_name) {
          data.emergency_contact_full_name = `${data.emergency_contact_first_name} ${data.emergency_contact_last_name}`;
        }
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (key === 'profile_image' || key === 'id_image') {
            const fileObject = extractFileObject(value);
            if (fileObject) formData.append(key, fileObject);
          } else {
            formData.append(key, value as any);
          }
        });
        formData.set('birthdate', dayjs(data.birthdate).format('MM/DD/YYYY'));
        postPlayerRegistration(formData)
          .then(() => {
            setOpenSuccess(true);
          })
          .finally(() => {
            setRequestsLoading((l) => ({ ...l, loadingSave: false }));
          });
      } else {
        await form.validateFields(STEP_FIELDS[step]);
        if (step === 2) {
          setRequestsLoading((l) => ({ ...l, loadingEmailCheck: true }));
          postCheckEmail({ email: data.email })
            .then(() => {
              form.setFields([{ name: 'email', errors: [] }]);
              setStep((s) => s + 1);
            })
            .catch(({ email }) => {
              form.setFields([{ name: 'email', errors: email }]);
            })
            .finally(() => {
              setRequestsLoading((l) => ({ ...l, loadingEmailCheck: false }));
            });
        } else if (step === 5) {
          setRequestsLoading((l) => ({ ...l, loadingFaceValidation: true }));

          const profileImageFile = extractFileObject(data.profile_image);

          if (!profileImageFile) {
            form.setFields([{ name: 'profile_image', errors: [t('error_profile_photo')] }]);
            setRequestsLoading((l) => ({ ...l, loadingFaceValidation: false }));
            return;
          }

          const errors = await validateFace(profileImageFile, lang === 'es');
          form.setFields([{ name: 'profile_image', errors }]);
          if (!errors.length) setStep((s) => s + 1);
          setRequestsLoading((l) => ({ ...l, loadingFaceValidation: false }));
        } else {
          setStep((s) => s + 1);
        }
      }
    } catch (e) {
      //
    }
  };

  const openHomePage = () => {
    navigate(PAGES.HOME);
  };

  const onValuesChange = ({ language }: any) => {
    if (!isFormTouched) setIsFormTouched(true);
    if (language) setLang(language);
  };

  useEffect(() => {
    setLoadingModel(true);
    loadModel(() => setLoadingModel(false));
  }, [setRequestsLoading]);

  return (
    <LanguageContext.Provider value={{ lang, t }}>
      <div className="page-player-registration">
        <Layout className="page-player-registration__content">
          <Row>
            <Col lg={{ flex: '304px' }} md={{ flex: '100%' }} span={24}>
              <SiteBranding isTouched={isFormTouched} lang={lang} />
            </Col>
            {openSuccess ? (
              <SuccessMessage />
            ) : (
              <Col className="form-block" lg={{ flex: '1' }} span={24}>
                <Row className="form-block__header" justify="space-between">
                  <Col sm={{ span: 24 }} md={{ flex: 'none' }}>
                    <Typography.Title level={3}>{t('title_create_profile')}</Typography.Title>
                  </Col>
                  {!!step && (
                    <Col sm={{ span: 24 }} md={{ flex: 'none' }}>
                      <Typography.Text>
                        <b>{step}</b>
                        <span>
                          {' /10 '}
                          {t('test_steps')}
                        </span>
                      </Typography.Text>
                    </Col>
                  )}
                </Row>
                <Form
                  form={form}
                  initialValues={INITIAL_VALUES}
                  onValuesChange={onValuesChange}
                  validateTrigger="onBlur"
                  className="form-block__content"
                >
                  <Row>
                    <Col span={24}>
                      <PlayerRegistrationSteps step={step} />
                    </Col>
                  </Row>
                  <Row gutter={8} justify="end" className="form-block__footer">
                    <Col>
                      {step ? (
                        <Button size="large" type="default" icon={<IconBack />} onClick={prev} />
                      ) : (
                        <Button size="large" type="default" icon={<IconBack />} onClick={openHomePage}>
                          {t('button_back')}
                        </Button>
                      )}
                    </Col>
                    <Col>
                      {step === 10 ? (
                        <Button size="large" onClick={next} iconPosition="end" loading={loading}>
                          {t('button_create_profile')}
                        </Button>
                      ) : (
                        <Button
                          size="large"
                          icon={<IconNext />}
                          iconPosition="end"
                          onClick={next}
                          loading={loading}
                          disabled={loadingModel && step === 5}
                        >
                          {t('button_next')}
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Form>
              </Col>
            )}
          </Row>
        </Layout>
      </div>
    </LanguageContext.Provider>
  );
};

const PlayerRegistrationSteps = ({ step }: { step: number }) => {
  switch (step) {
    case 0:
      return <Step0 />;
    case 1:
      return <Step1 />;
    case 2:
      return <Step2 />;
    case 3:
      return <Step3 />;
    case 4:
      return <Step4 />;
    case 5:
      return <Step5 />;
    case 6:
      return <Step6 />;
    case 7:
      return <Step7 />;
    case 8:
      return <Step8 />;
    case 9:
      return <Step9 />;
    case 10:
      return <Step10 />;
    default:
      return null;
  }
};

const SuccessMessage = () => {
  const { t } = useLang();
  return (
    <Col flex="1" className="player-registration__message player-registration__message-success">
      <div>
        <IconSuccess className="icon" />
        <Typography.Title className="title" level={3}>
          {t('title_player_reg_success')}
        </Typography.Title>
        <Typography.Text className="description">
          {t('text_player_reg_success_1')}
          <br />
          <br />
          {t('text_player_reg_success_2')}
        </Typography.Text>
        <Link to={PAGES.SIGN_IN}>
          <Button size="large" icon={<IconArrowLeft />}>
            {t('button_back_to_sign_in')}
          </Button>
        </Link>
      </div>
    </Col>
  );
};
